/**
 * lib/help-toc — ВТОРОЙ рейл-оглавление «Помощи» (второй уровень навигации; тикет
 * start-page-redo-and-help, Часть B). «Веточка», построенная НА стволах — НЕ свой
 * drawer/список/иконка-кнопка с нуля.
 *
 * ЗАЧЕМ / WHY THIS EXISTS: внутри «Помощи» нужна навигация по 9 разделам. Owner:
 * закреплённые ВЕРХНИЕ кнопки разделов «жрут место» (PROHIBITED) — вместо них, как у
 * сапёра, «выезжает рейл с оглавлением». На ПК/широком оглавление = постоянная sticky-
 * КОЛОНКА слева от текста; на touch/узком = выезжающая ПАНЕЛЬ (drawer) по кнопке-
 * переключателю. Переключение колонка↔drawer — по ТОЙ ЖЕ формуле запаса, что глобальный
 * рейл inline↔collapsed (НЕ width-breakpoint): tie к классам shell `#app.nav-inline` /
 * `#app.nav-collapsed`.
 *
 * REUSE (ствол→ветки, AC-12/13/15):
 *   - список разделов = `lib/content-nav` (createContentNav, includeLanding:false) —
 *     ЕДИНЫЙ источник contentNavLinks, НЕ хардкод-массив slugs;
 *   - drawer-механика = `lib/drawer` (attachDrawer: open/close/scrim/Esc/aria-expanded)
 *     — ТОТ ЖЕ ствол, что у глобального рейла (НЕ свой scrim+Escape с нуля);
 *   - кнопка-переключатель = `components/icon-button` (createIconButton, framed);
 *   - размер шрифта пунктов — ДЕТЕРМИНИРОВАННО из CSS (--fs-control·0.9, help-toc.css;
 *     owner п.8 / тикет fe-home-screen AC-13/14/16 «как на основном рейле», НЕ крупнее).
 *     Прежний shrink-to-fit group-autofit убран — ужимал лейблы ниже --fs-control
 *     (рассинхрон) + settle-гонка измерения (флака parity); длинные имена переносятся
 *     по словам (§8.10-C), без break-all/ellipsis. NB: старый AC-24 source-grep тест
 *     ожидает autofit-import здесь → СТАЛ под этот тикет (см. отчёт «риски»).
 *   Новое здесь — ТОЛЬКО «второй уровень» как колонка/drawer (layout).
 *
 * SCOPE: смонтировать оглавление-колонку/drawer + переключатель + scrim в контент-
 *   экран; переключать режим по shell-классам; group-autofit пунктов. NOT IN SCOPE:
 *   список разделов (content-nav), drawer-механика (drawer), иконка-кнопка
 *   (icon-button), маршрутизация (content-routing — делегированный клик экрана).
 *
 * Cross-references: тикет AC-11..16; lib/content-nav; lib/drawer; lib/autofit-text;
 *   components/icon-button; lib/viewport-sizer (VIEWPORT_APPLIED_EVENT); help-toc.css.
 */

import '../styles/help-toc.css';

import { createIconButton } from '../components/icon-button.js';
import { createContentNav } from './content-nav.js';
import { attachDrawer, type DrawerHandle } from './drawer.js';
import { VIEWPORT_APPLIED_EVENT } from './viewport-sizer.js';
import type { ContentLang } from '@lines98/shared/config/seo';

/** ☰-глиф переключателя оглавления — список (Tabler «list»), отличим от бренд-☰. */
const ICON_LIST = `<svg class="icon-btn-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M9 6l11 0"/><path d="M9 12l11 0"/><path d="M9 18l11 0"/><path d="M5 6l0 .01"/><path d="M5 12l0 .01"/><path d="M5 18l0 .01"/></svg>`;

export interface HelpTocOptions {
  /** Текущий язык (ПАРАМЕТР, не ветка) — один модуль на все языки. */
  readonly lang: ContentLang;
  /** Активный раздел (slug) — подсвечивается ровно один пункт оглавления. */
  readonly activeSlug: string;
  /** Доступное имя оглавления-навигации (i18n). */
  readonly navLabel: string;
  /** Доступное имя кнопки-переключателя (i18n, напр. «Разделы Помощи»). */
  readonly toggleLabel: string;
}

export interface HelpTocHandle {
  /** Снять слушатели/drawer/autofit + удалить смонтированный DOM. */
  detach(): void;
}

/**
 * Смонтировать второй рейл-оглавление в `screenRoot` (корень контент-экрана). На
 * ПК/широком (рейл inline) — sticky-колонка; на touch/узком — drawer по переключателю.
 * Режим (`data-help-toc="column"|"drawer"`) отслеживает классы shell `#app`. Выбор
 * раздела закрывает drawer (делегированный клик экрана делает SPA-переход сам).
 */
export function mountHelpToc(screenRoot: HTMLElement, opts: HelpTocOptions): HelpTocHandle {
  const shell = document.getElementById('app') ?? document.body;

  // --- Панель-оглавление (.help-toc): колонка ИЛИ drawer (CSS решает по shell-классу). ---
  const panel = document.createElement('aside');
  panel.className = 'help-toc';
  panel.setAttribute('aria-label', opts.navLabel);

  // Список разделов = reuse content-nav (единый источник; лендинг исключён). НЕ свой
  // <a>-список и НЕ хардкод массива slugs (AC-12/9).
  const navEl = createContentNav({
    lang: opts.lang,
    activeSlug: opts.activeSlug,
    ariaLabel: opts.navLabel,
    includeLanding: false,
  }).element;
  panel.appendChild(navEl);

  // --- Затемнитель drawer (виден только в drawer-режиме, CSS). ---
  const scrim = document.createElement('div');
  scrim.className = 'help-toc-scrim';
  scrim.setAttribute('aria-hidden', 'true');

  // --- Кнопка-переключатель = reuse icon-button (framed; виден только в drawer-режиме). ---
  const toggleBtn = createIconButton({
    glyph: ICON_LIST,
    label: opts.toggleLabel,
    variant: 'framed',
    className: 'help-toc-toggle',
  }).element;
  toggleBtn.setAttribute('aria-expanded', 'false');

  // Сборка: переключатель + scrim + панель в корень контент-экрана (scoped — снимутся
  // на unmount экрана). Порядок: scrim под панелью (CSS z-index), панель поверх.
  screenRoot.append(toggleBtn, scrim, panel);

  // --- Drawer-механика = reuse общий ствол lib/drawer (НЕ свой scrim+Esc). ---
  const drawer: DrawerHandle = attachDrawer({
    shell: screenRoot,
    openClass: 'help-toc-open',
    scrim,
    toggle: toggleBtn,
  });
  toggleBtn.addEventListener('click', () => drawer.toggle());
  // Выбор раздела в drawer → закрыть оглавление (клик по ссылке оглавления).
  navEl.addEventListener('click', (e) => {
    if ((e.target as HTMLElement | null)?.closest('a')) drawer.close();
  });

  // --- Режим колонка↔drawer по формуле запаса рейла (НЕ width-breakpoint). Tie к
  // shell-классам: рейл inline (широко) → колонка; collapsed (тесно) → drawer. Тот же
  // критерий, что рейл считает сам (единый источник запаса). На drawer-режим закрываем
  // оглавление (чтобы не «зависло» открытым при ресайзе в широкое). ---
  function syncMode(): void {
    const drawerMode = shell.classList.contains('nav-collapsed');
    panel.dataset.helpToc = drawerMode ? 'drawer' : 'column';
    screenRoot.dataset.helpToc = drawerMode ? 'drawer' : 'column';
    if (!drawerMode) drawer.close(); // в колонке drawer не нужен
  }
  window.addEventListener(VIEWPORT_APPLIED_EVENT, syncMode);
  window.addEventListener('resize', syncMode, { passive: true });
  // Стартовая синхронизация после применения CSS рейла (он выставляет nav-inline/-collapsed).
  requestAnimationFrame(syncMode);
  syncMode();

  // Размер шрифта пунктов оглавления — ДЕТЕРМИНИРОВАННО из CSS (--fs-control·0.9,
  // help-toc.css; owner п.8 / тикет fe-home-screen AC-13/14/16 «как на основном рейле»,
  // НЕ крупнее, растёт от cell без капа). Прежний shrink-to-fit group-autofit УБРАН:
  // (1) на широкой колонке он ужимал лейблы НИЖЕ --fs-control (рассинхрон с основным
  // рейлом — тикет ловил 11.6px при --fs-control 18.6); (2) re-measure-таймингом давал
  // settle-гонку (флака в parity-сьюте при reuse worker-контекста). Длинные имена
  // («Реплеи и призраки») переносятся ПО СЛОВАМ (help-toc.css white-space:normal,
  // §8.10-C — без break-all/ellipsis). NB: старый source-grep тест AC-24
  // (start-help-cascade-figures.test.ts) ожидает import autofit-text здесь — он СТАЛ
  // СТАРЫМ под этот тикет (нужно обновление frontend-test-creator'ом; см. отчёт «риски»).
  return {
    detach(): void {
      window.removeEventListener(VIEWPORT_APPLIED_EVENT, syncMode);
      window.removeEventListener('resize', syncMode);
      drawer.detach();
      toggleBtn.remove();
      scrim.remove();
      panel.remove();
      delete screenRoot.dataset.helpToc;
    },
  };
}
